<script setup lang="ts">
import { defineEmits, defineProps, ref, reactive, computed } from "vue";
import { useTranslate } from "@/composables/useTranslate";
import SelectMenu from "../Re-usable/SelectMenu.vue";
import SpinnerLoader from "../Loaders/SpinnerLoader.vue";

type Props = {
  isReportDialogOpen: boolean;
  selectedMonth: any;
  selectedYear: any;
  selectedUrl: any;
  months: any;
  years: any;
  isEdit: boolean;
  isLoading: boolean;
};

const props = defineProps<Props>();

type Emits = {
  (event: "onClose"): void;
  (event: "increaseDialogStage"): void;
  (event: "onSubmitBonus", month: string, year: string, url: string): void;
};

const selectedMonth = ref(props.selectedMonth);
const selectedYear = ref(props.selectedYear);
const selectedUrl = ref(props.selectedUrl);
const isLoading = computed(() => props.isLoading);

const emits = defineEmits<Emits>();
const { t } = useTranslate();

const errors = reactive({
  selectedUrl: "",
});

const validate = () => {
  let isValid = [];
  errors.selectedUrl = "";

  if (!selectedUrl.value) {
    errors.selectedUrl = t("installers.enter_report_url").value;
    isValid.push(false);
  } else {
    isValid.push(true);
    errors.selectedUrl = "";
  }

  return isValid.filter((item: boolean) => !item).length === 0;
};

const onSubmit = async () => {
  if (!validate()) return;

  emits(
    "onSubmitBonus",
    selectedMonth.value,
    selectedYear.value,
    selectedUrl.value
  );
};

const handleClose = () => {
  emits("onClose");
};
</script>
<template>
  <Transition name="nested">
    <div class="w-full h-screen fixed top-0 left-0 bg-black/75 z-[1000]">
      <div
        class="w-[588px] min-h-[458px] px-[20px] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-[7px] bg-white z-[1000]"
      >
        <div
          class="flex items-center justify-between py-[16px] mb-[16px] border-b border-[#E3E1EA]"
        >
          <slot name="custom-header" />
          <img
            class="h-[12px] w-[12px] cursor-pointer"
            src="@/assets/close-icon-2.svg"
            alt=""
            @click="handleClose()"
          />
        </div>
        <div class="mb-[24px]">
          <div>
            <p class="mb-[6px] text-[16px] text-[#898790]">
              {{ t("installers.choose_month") }}
            </p>
            <div class="relative w-full">
              <div class="max-h-[300px] overflow-y-auto">
                <SelectMenu
                  v-model="selectedMonth"
                  :options="months"
                  :disabled="isEdit"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="mb-[24px]">
          <div>
            <p class="mb-[6px] text-[16px] text-[#898790]">
              {{ t("installers.choose_year") }}
            </p>
            <div class="relative w-full">
              <div class="max-h-[300px] overflow-y-auto">
                <SelectMenu
                  v-model="selectedYear"
                  :options="years"
                  :disabled="isEdit"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="pb-[16px] border-b border-[#E3E1EA]">
          <div>
            <p class="mb-[6px] text-[16px] text-[#898790]">
              {{ t("installers.report_url") }}
            </p>
            <div class="relative w-full">
              <!-- Select -->
              <input
                v-model="selectedUrl"
                :placeholder="t('installers.add_link').value"
                class="w-full appearance-none outline-none rounded-[6px] bg-[#F9F8F6] p-[12px]"
              />
              <p
                v-if="errors.selectedUrl"
                class="text-sm text-red-400 font-medium mt-1"
              >
                {{ errors.selectedUrl }}
              </p>
            </div>
          </div>
        </div>
        <div class="py-[12px] flex items-center justify-end">
          <button
            @click="onSubmit"
            class="w-[150px] flex items-center justify-center p-[12px] gap-[8px] text-[16px] text-white font-semibold bg-[#393840] rounded-[7px]"
          >
            <div v-if="isLoading" class="flex items-center justify-center">
              <SpinnerLoader />
            </div>
            <span v-else>{{ t("global.upload") }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>
